import { useSearchParams } from "react-router-dom";
import { useSavedRedirector } from "event/hooks/useSavedRedirector";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PronosticV2Service } from "services/pronosticv2.service";
import { PronosticV2 } from "pronosticsv2/types/pronosticv2.model";
import type { Event } from "event/types/event.model";

function EventQRScanViewV2() {
  // Extract URL params
  const [searchParams] = useSearchParams();
  const subPlaceId = searchParams.get("subPlaceId");
  const placeId = searchParams.get("placeId");
  const L1 = searchParams.get("L1");
  const L2 = searchParams.get("L2");
  const L3 = searchParams.get("L3");
  const L4 = searchParams.get("L4");
  const bingoGameId = searchParams.get("bingoGameId");

  console.log("bingoGameId", bingoGameId);
  console.log("L1", L1);

  // Suppress unused variables
  void L2;
  void L3;
  void L4;
  void placeId;

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [dots, setDots] = useState(".");

  // Hooks
  const [redirector, setRedirector, deleteRedirector] = useSavedRedirector();
  const navigate = useNavigate();
  void redirector;

  // Services
  const pronosticV2Service = useMemo(() => new PronosticV2Service(), []);
  // const bingoGameService = new BingoGameService();

  // Helper function to check if subplace exists in pronostic
  const checkSubplaceInPronostic = useCallback(
    async (pronosticV2Id: string): Promise<boolean> => {
      try {
        const getPronosticV2ByIdData = { pronosticV2Id: pronosticV2Id };
        const pronosticV2 = (await pronosticV2Service.getPronosticV2ById(
          getPronosticV2ByIdData,
        )) as unknown as PronosticV2;

        if (!pronosticV2) return false;

        const subplaces =
          pronosticV2?.commonSettings.general.moduleSettings.place.subPlaces;
        return !!subplaces?.find((subplace) => subplace.id === subPlaceId);
      } catch (error) {
        console.error("Failed to fetch pronostic:", error);
        return false;
      }
    },
    [subPlaceId, pronosticV2Service],
  );

  // Main processing function - handles everything in sequence
  const processEvents = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);

      // Handball path: L2=handball with no subPlaceId — find next upcoming event today
      if (L2?.toLowerCase() === "handball" && !subPlaceId) {
        const events =
          (await pronosticV2Service.getAllEvents()) as unknown as Event[];
        const today = new Date().toDateString();
        const now = new Date();

        const upcomingTodayEvents = events.filter((event) => {
          const eventDate = new Date(event.kickoff_date).toDateString();
          return (
            eventDate === today &&
            event.organization === L1 &&
            new Date(event.kickoff_date) > now
          );
        });

        console.log("handball upcomingTodayEvents", upcomingTodayEvents);

        if (upcomingTodayEvents.length === 0) {
          setErrorMessage("Il n'y a pas d'événement ouvert pour le moment");
        } else {
          upcomingTodayEvents.sort(
            (a, b) =>
              new Date(a.kickoff_date).getTime() -
              new Date(b.kickoff_date).getTime(),
          );
          navigate(`/event/${upcomingTodayEvents[0].id}`);
        }
        return;
      }

      // Step 1: Validate required params
      if (!subPlaceId && !bingoGameId) {
        setErrorMessage(
          "Vous n'êtes pas localisé correctement, veuillez contacter l'organisateur",
        );
        return;
      }

      // Step 2: Fetch events
      const events =
        (await pronosticV2Service.getAllEvents()) as unknown as Event[];
      console.log("Fetched events:", events);

      const today = new Date().toDateString();
      const todayEvents = events.filter((event) => {
        const eventDate = new Date(event.kickoff_date).toDateString();

        if (bingoGameId) {
          return (
            eventDate === today &&
            event.organization === L1 &&
            event.bingo_game_display &&
            event.bingo_game_id &&
            event.bingo_game_id === bingoGameId
          );
        } else {
          return (
            eventDate === today &&
            event.organization === L1 &&
            event.pronostics_v2_display &&
            event.pronostics_v2_id
          );
        }
      });

      // Step 5: For pronostic events, validate subplaces
      let validEvents = [];
      if (bingoGameId) {
        // For bingo games, all filtered events are valid
        validEvents = todayEvents;
      } else {
        // For pronostic events, check each event's subplaces
        for (const event of todayEvents) {
          if (event.pronostics_v2_id) {
            const hasValidSubplace = await checkSubplaceInPronostic(
              event.pronostics_v2_id,
            );
            if (hasValidSubplace) {
              validEvents.push(event);
            }
          }
        }
      }

      console.log("filteredEvents", validEvents);

      // Step 6: Handle results
      if (validEvents.length === 0) {
        setErrorMessage("Il n'y a pas d'événement ouvert pour le moment");
      } else if (validEvents.length === 1) {
        // Single event - redirect
        if (bingoGameId) {
          setRedirector({
            bingoGameId: bingoGameId,
            redirectTo: "bingogame",
          });
        } else {
          setRedirector({
            subPlaceId: subPlaceId as string,
            redirectTo: "pronosticsV2",
          });
        }
        navigate(`/event/${validEvents[0].id}`);
      } else {
        // Multiple events - this shouldn't happen with proper location
        setErrorMessage(
          "Vous n'êtes pas localisé correctement, veuillez contacter l'organisateur",
        );
      }
    } catch (error) {
      console.error("Failed to process events:", error);
      setErrorMessage("Erreur lors du chargement des événements");
    } finally {
      setIsLoading(false);
    }
  }, [
    subPlaceId,
    bingoGameId,
    L1,
    L2,
    checkSubplaceInPronostic,
    setRedirector,
    navigate,
    pronosticV2Service,
  ]);

  // useEffect 1: Initialize everything on mount
  useEffect(() => {
    // Clear any existing redirector
    deleteRedirector();

    // Start processing
    processEvents();

    // Setup page hide handler to clear redirector
    const handlePageHide = () => deleteRedirector();
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [deleteRedirector, processEvents]);

  // useEffect 2: Animate dots when loading (only when no error)
  useEffect(() => {
    if (errorMessage || !isLoading) return;

    const interval = setInterval(() => {
      setDots((prev) => {
        if (prev === ".") return "..";
        if (prev === "..") return "...";
        return ".";
      });
    }, 400);

    return () => clearInterval(interval);
  }, [errorMessage, isLoading]);

  return (
    <div
      style={{
        margin: 0,
        padding: 0,
        backgroundColor: "#8C8C8C",
        fontFamily: "Barlow, sans-serif",
        height: "100vh",
        width: "100vw",
      }}
    >
      <div
        style={{
          display: "flex",
          paddingTop: "30px",
          width: "100%",
          color: "#000000",
          fontSize: "34px",
          fontWeight: "bold",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src="https://livequiz-webappbucket302bdd47-t9jg9ywvk8ck.s3.eu-west-3.amazonaws.com/static/media/PES/PES_black.png"
            alt="Logo"
            style={{ maxWidth: "70%", maxHeight: "70%" }}
          />
        </div>
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "left",
            paddingLeft: "30px",
            fontSize: "20px",
          }}
        >
          PLAY EVENT SOLUTIONS
        </div>
      </div>

      {errorMessage ? (
        <div
          style={{
            color: "#8F0C0C",
            width: "70%",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            fontSize: "28px",
            backgroundColor: "rgba(217, 217, 217, 0.5)",
            padding: "20px 0px",
            borderRadius: "28px",
            border: "rgba(143, 12, 12, 1) solid 3px",
            maxWidth: "90%",
          }}
        >
          <p style={{ fontWeight: "bold", fontSize: "24px" }}>Erreur</p>
          <p style={{ fontSize: "16px" }}>{errorMessage}</p>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            width: "70%",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            fontSize: "28px",
            padding: "20px 0px",
            maxWidth: "90%",
            paddingLeft: "15px",
          }}
        >
          <div style={{ width: "60%", display: "flex", justifyContent: "end" }}>
            CONNEXION
          </div>
          <div style={{ width: "40%" }}>{dots}</div>
        </div>
      )}
    </div>
  );
}

export { EventQRScanViewV2 };
